// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { formatHumanName, getQuestionnaireAnswers } from '@medplum/core';
import type {
  BundleEntry,
  ClinicalImpression,
  Composition,
  CoverageEligibilityResponse,
  Observation,
  Organization,
  Patient,
  QuestionnaireResponse,
  Resource,
} from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import type { Comorbidity, ExclusionCriterion, TriageIntakeSortColumnKey } from '@polarissleep/shared';
import {
  COMPOSITION_TYPE_SYSTEM,
  calculateBmiFromObservations,
  calculateBmiFromStopBangQR,
  calculateEpworthScore,
  calculateStopBangScoreFromQR,
  getComorbiditiesFromQR,
  getExclusionCriteriaFromQR,
  isSelfPayPatient,
  isTestPatient,
  PATIENT_SYSTEM_STATUS,
  PATIENT_TRIAGE_STATUS,
  POLARIS_SYSTEM_STATUS_TAG_SYSTEM,
  POLARIS_TRIAGE_STATUS_TAG_SYSTEM,
  SELF_PAY_TAG_SYSTEM,
  TAG_SYSTEM,
  TEST_PATIENT_TAG_CODE,
  TRIAGE_COMPOSITION_TYPES,
  TRIAGE_OBS_CATEGORY,
  TRIAGE_OBS_CATEGORY_SYSTEM,
  TRIAGE_OBS_CODES,
  TRIAGE_TAGS,
  TRIAGE_TAGS_LABELS,
  type TriageSortState,
} from '@polarissleep/shared';
import { useCallback, useEffect, useState } from 'react';

export interface TriageCompositionData {
  compositionId: string;
  patientId: string | undefined;
  patientName: string;
  sexAtBirth: string | undefined;
  insuranceOrganization: string | undefined;
  isSelfPay: boolean;
  isTestPatient: boolean;
  coverageEligibilityResponse: CoverageEligibilityResponse | undefined;
  stopBangScore: number | undefined;
  stopBangComplete?: boolean;
  epworthScore: number | undefined;
  epworthComplete?: boolean;
  bmi: number | undefined;
  age: number | undefined;
  date: string | undefined;
  exclusions: ExclusionCriterion[];
  comorbidities: Comorbidity[];
  recommendation: string | undefined;
  triageSummary: string | undefined;
  triageStatus: string | undefined;
  ccEvaluationStatus: 'success' | 'failure' | undefined;
  ccEvaluatedBy: string | undefined;
  systemStatus: string | undefined;
}

function triageTag(code: string): string {
  return `${POLARIS_TRIAGE_STATUS_TAG_SYSTEM}|${code}`;
}

const DEPRECATED_TRIAGE_QUEUE_STATUS_CODES = new Set<string>([
  PATIENT_TRIAGE_STATUS.followUp30Day,
  PATIENT_TRIAGE_STATUS.followUp60Day,
  PATIENT_TRIAGE_STATUS.followUp90Day,
  PATIENT_TRIAGE_STATUS.followUp150Day,
  PATIENT_TRIAGE_STATUS.followUp180Day,
  PATIENT_TRIAGE_STATUS.miscFollowUp,
  PATIENT_TRIAGE_STATUS.remoteMonitoring,
]);

const ACTIVE_TRIAGE_QUEUE_STATUS_CODES = Object.values(PATIENT_TRIAGE_STATUS).filter(
  (code) => !DEPRECATED_TRIAGE_QUEUE_STATUS_CODES.has(code)
);

const INTAKE_STAGE_TAGS = [PATIENT_TRIAGE_STATUS.intake, PATIENT_TRIAGE_STATUS.hsatApprovedCm].map(triageTag).join(',');

const TESTING_STAGE_TAGS = [
  PATIENT_TRIAGE_STATUS.hsat,
  PATIENT_TRIAGE_STATUS.hsatApprovedNp,
  PATIENT_TRIAGE_STATUS.hsatOrdered,
  PATIENT_TRIAGE_STATUS.hsatResults,
]
  .map(triageTag)
  .join(',');

const STAGE_TAG_MAP: Record<string, string> = {
  // The legacy follow-up and RPM triage tags are deprecated in the intake/triage
  // queue and are now tracked via their dedicated queues/workflows.
  'All Stages': ACTIVE_TRIAGE_QUEUE_STATUS_CODES.map(triageTag).join(','),
  Intake: INTAKE_STAGE_TAGS,
  Testing: TESTING_STAGE_TAGS,
  Denied: triageTag(PATIENT_TRIAGE_STATUS.denied),
};

interface UseTriageQueueOptions {
  page: number;
  pageSize: number;
  activeStage?: string;
  search?: string;
  sort?: TriageSortState;
  hideTestPatients?: boolean;
}

interface UseTriageQueueReturn {
  compositions: TriageCompositionData[];
  loading: boolean;
  total: number;
  /**
   * Always `true` in Phase 4+. The Observation-backed sort engine works on the
   * default (unfiltered) CompositionList branch; filter+sort interaction is
   * intentionally deferred (see top-of-file comment), but the column arrows
   * remain enabled regardless so the URL+UI contract is uniform.
   */
  sortSupported: boolean;
}

interface CoveragePayorGraphQLResult {
  reference?: string;
  resource?: Organization;
}

interface CoverageGraphQLResult {
  payor?: CoveragePayorGraphQLResult[];
}

interface GRGraphQLResult {
  status?: string;
  moduleUri?: string;
  extension?: { url?: string; valueReference?: { resource?: { name?: { given?: string[]; family?: string }[] } } }[];
}

interface PatientGraphQLResult extends Patient {
  CoverageList?: CoverageGraphQLResult[];
  CoverageEligibilityResponseList?: CoverageEligibilityResponse[];
  heightObs?: Observation[];
  weightObs?: Observation[];
  triageCI?: ClinicalImpression[];
  hsatGR?: GRGraphQLResult[];
}

interface PatientWithCompositionsGraphQLResult extends PatientGraphQLResult {
  compositions?: CompositionGraphQLResult[];
}

interface CompositionSectionEntryGraphQL {
  reference?: string;
  resource?: Resource;
}

interface CompositionSectionGraphQL {
  title?: string;
  entry?: CompositionSectionEntryGraphQL[];
}

interface CompositionLike {
  id?: string;
  date?: string;
  section?: CompositionSectionGraphQL[];
  subject?: {
    reference?: string;
    resource?: Resource;
  };
}

interface CompositionGraphQLResult extends CompositionLike {
  subject?: {
    reference?: string;
    resource?: PatientGraphQLResult;
  };
}

/**
 * Observation joined back to its source triage Composition + Patient.
 * The sort branch projects this from `ObservationList { derivedFrom { resource { ...Composition } subject { ...Patient } } }`.
 */
interface ObservationWithCompositionGraphQLResult {
  id?: string;
  valueQuantity?: { value?: number; unit?: string; code?: string };
  valueCodeableConcept?: { coding?: { system?: string; code?: string; display?: string }[] };
  subject?: { reference?: string; resource?: PatientGraphQLResult };
  derivedFrom?: { reference?: string; resource?: CompositionGraphQLResult }[];
}

const BIRTHSEX_EXTENSION_URL = 'http://hl7.org/fhir/us/core/StructureDefinition/us-core-birthsex';
const PERFORMER_EXTENSION_URL = 'http://polarissleep.com/extensions/performer';
const HSAT_ELIGIBILITY_MODULE_URI = 'http://polarissleep.com/guidance/hsat-eligibility';

const STOP_BANG_REQUIRED_LINK_IDS = [
  'snore-loudly',
  'tired-fatigued',
  'stop-breathing',
  'high-blood-pressure',
  'height',
  'weight',
  'age-50-or-older',
  'neck-circumference-over-16',
  'male-or-assigned-male',
] as const;

const EPWORTH_REQUIRED_LINK_IDS = [
  'ess-reading',
  'ess-television',
  'ess-inactive',
  'ess-passenger',
  'ess-lying-down',
  'ess-talking',
  'ess-after-meal',
  'ess-traffic',
] as const;

/**
 * GraphQL filter value for the `Observation.category` token search — nullish marker series.
 * Shape: `<category-system>|patient-latest-triage-nullish`.
 */
const PATIENT_LATEST_NULLISH_CATEGORY_FILTER = `${TRIAGE_OBS_CATEGORY_SYSTEM}|${TRIAGE_OBS_CATEGORY.patientLatestNullish}`;

/**
 * Filter out compositions where the patient's system status is 'inactive'.
 * NOTE: `total` is the server-side CompositionConnection count and includes inactive
 * (referred) patients. After Phase 5, compositions.length can be less than total
 * when referred patients exist. v1 known limitation (§6 of the implementation plan).
 */
function filterOutInactivePatients(data: TriageCompositionData[]): TriageCompositionData[] {
  return data.filter((c) => c.systemStatus !== PATIENT_SYSTEM_STATUS.inactive);
}

function applyFilters(data: TriageCompositionData[], hideTestPatients: boolean): TriageCompositionData[] {
  const withoutInactive = filterOutInactivePatients(data);
  return hideTestPatients ? withoutInactive.filter((c) => !c.isTestPatient) : withoutInactive;
}

function hasAnswer(qr: QuestionnaireResponse, linkId: string): boolean {
  return qr.item?.some((item) => item.linkId === linkId && (item.answer?.length ?? 0) > 0) ?? false;
}

function isComplete(qr: QuestionnaireResponse, requiredLinkIds: readonly string[]): boolean {
  return requiredLinkIds.every((linkId) => hasAnswer(qr, linkId));
}

function selectLatestResponse(
  responses: QuestionnaireResponse[],
  markerLinkId: string
): QuestionnaireResponse | undefined {
  return responses
    .filter((qr) => qr.item?.some((item) => item.linkId === markerLinkId))
    .sort((a, b) => {
      const authoredA = a.authored ?? '';
      const authoredB = b.authored ?? '';
      return authoredA.localeCompare(authoredB) || (a.id ?? '').localeCompare(b.id ?? '');
    })
    .at(-1);
}

export function mapToTriageCompositionData(
  comp: CompositionLike | undefined,
  patient: PatientWithCompositionsGraphQLResult | undefined
): TriageCompositionData {
  const patientName = patient?.name && patient.name.length > 0 ? formatHumanName(patient.name[0]) : 'Unknown Patient';

  const insuranceOrganization = patient?.CoverageList?.flatMap(
    (coverage) => coverage.payor?.map((payor) => payor.resource?.name) ?? []
  ).find(Boolean);
  const coverageEligibilityResponse = patient?.CoverageEligibilityResponseList?.find(
    (response) => (response.insurance?.length ?? 0) > 0
  );

  let age: number | undefined;
  if (patient?.birthDate) {
    const birth = new Date(patient.birthDate);
    const today = new Date();
    age = today.getFullYear() - birth.getFullYear();
    if (
      today.getMonth() < birth.getMonth() ||
      (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())
    ) {
      age--;
    }
  }

  const allQRs = (comp?.section ?? [])
    .flatMap((s) => s.entry ?? [])
    .map((e) => e.resource)
    // GraphQL inline resource projections omit FHIR's resourceType discriminator.
    // The requested `item` field is the QuestionnaireResponse shape marker here.
    .filter((resource): resource is QuestionnaireResponse => resource != null && 'item' in resource);

  const stopBangQR = selectLatestResponse(allQRs, 'snore-loudly');
  const epworthQR = selectLatestResponse(allQRs, 'ess-reading');
  const exclusionQRs = allQRs.filter((qr) =>
    qr.item?.some(
      (item) =>
        item.linkId === 'clinical-conditions' ||
        item.linkId === 'previous-sleep-study' ||
        item.linkId === 'cpap-bipap-use' ||
        item.linkId === 'oral-appliance-use' ||
        item.linkId === 'sleep-apnea-surgery' ||
        item.linkId === 'prior-test-failed'
    )
  );
  const diagnosesQR = allQRs.find((qr) => qr.item?.some((item) => item.linkId === 'diagnoses'));

  const sexAtBirth = patient?.extension?.find((e) => e.url === BIRTHSEX_EXTENSION_URL)?.valueCoding?.code;
  const stopBangComplete = stopBangQR ? isComplete(stopBangQR, STOP_BANG_REQUIRED_LINK_IDS) : undefined;
  const epworthComplete = epworthQR ? isComplete(epworthQR, EPWORTH_REQUIRED_LINK_IDS) : undefined;
  const stopBangScore =
    stopBangQR && stopBangComplete ? calculateStopBangScoreFromQR(stopBangQR, sexAtBirth) : undefined;
  const epworthScore =
    epworthQR && epworthComplete ? calculateEpworthScore(getQuestionnaireAnswers(epworthQR)) : undefined;
  const bmi =
    calculateBmiFromObservations(patient?.heightObs?.[0], patient?.weightObs?.[0]) ??
    (stopBangQR ? calculateBmiFromStopBangQR(stopBangQR) : undefined);

  const exclusions = Array.from(
    new Map(
      exclusionQRs.flatMap((qr) => getExclusionCriteriaFromQR(qr)).map((exclusion) => [exclusion.code, exclusion])
    ).values()
  );
  const comorbidities = diagnosesQR ? getComorbiditiesFromQR(diagnosesQR) : [];

  const triageStatus = patient?.meta?.tag?.find((t) => t.system === POLARIS_TRIAGE_STATUS_TAG_SYSTEM)?.code;
  const systemStatus = patient?.meta?.tag?.find((t) => t.system === POLARIS_SYSTEM_STATUS_TAG_SYSTEM)?.code;

  const triageCI = patient?.triageCI?.[0];
  const triageTag = triageCI?.meta?.tag?.find((t) => t.system === TAG_SYSTEM);
  const recommendation = triageTag?.code ? TRIAGE_TAGS_LABELS[triageTag.code] : undefined;
  const triageSummary = triageCI?.summary;

  const hsatGR = patient?.hsatGR?.find((gr) => gr.moduleUri === HSAT_ELIGIBILITY_MODULE_URI);
  const grStatus = hsatGR?.status;
  const ccEvaluationStatus = grStatus === 'success' || grStatus === 'failure' ? grStatus : undefined;
  const performerExt = hsatGR?.extension?.find((e) => e.url === PERFORMER_EXTENSION_URL);
  const practitionerName = performerExt?.valueReference?.resource?.name?.[0];
  const ccEvaluatedBy = practitionerName ? formatHumanName(practitionerName) : undefined;

  return {
    // Fall back to a patient-keyed id so DataTable's `idAccessor="compositionId"`
    // stays unique across rows when the patient has no Composition. The synthetic
    // `no-comp:<patientId>` prefix avoids collision with any real Composition id.
    compositionId: comp?.id ?? (patient?.id ? `no-comp:${patient.id}` : ''),
    patientId: patient?.id,
    patientName,
    sexAtBirth,
    insuranceOrganization,
    isSelfPay: isSelfPayPatient(patient as Patient | undefined),
    isTestPatient: isTestPatient(patient as Patient | undefined),
    coverageEligibilityResponse,
    stopBangScore,
    stopBangComplete,
    epworthScore,
    epworthComplete,
    bmi,
    age,
    date: comp?.date,
    exclusions,
    comorbidities,
    recommendation,
    triageSummary,
    triageStatus,
    ccEvaluationStatus,
    ccEvaluatedBy,
    systemStatus,
  };
}

const PATIENT_FIELDS = `
  id
  birthDate
  meta { tag { system code } }
  extension {
    url
    valueCoding { code }
  }
  name { given family }
  CoverageList(_reference: beneficiary) {
    payor {
      reference
      resource {
        ... on Organization { id name }
      }
    }
  }
  CoverageEligibilityResponseList(_reference: patient) {
    id outcome disposition
    insurer { reference }
    insurance { coverage { reference } inforce }
  }
  heightObs: ObservationList(_reference: subject code: "8302-2" _count: 1 _sort: "-_lastUpdated") {
    valueQuantity { value unit }
  }
  weightObs: ObservationList(_reference: subject code: "29463-7" _count: 1 _sort: "-_lastUpdated") {
    valueQuantity { value unit }
  }
  hsatGR: GuidanceResponseList(_reference: subject _count: 5 _sort: "-_lastUpdated") {
    status
    moduleUri
    extension {
      url
      valueReference {
        resource {
          ... on Practitioner { name { given family } }
        }
      }
    }
  }
`;

const TRIAGE_CI_FRAGMENT = (triageTagFilter: string) => `
  triageCI: ClinicalImpressionList(_reference: subject _tag: "${triageTagFilter}" _count: 1 _sort: "-_lastUpdated") {
    summary
    meta { tag { system code } }
  }
`;

const COMPOSITION_SECTION_FIELDS = `
  id
  date
  section {
    title
    entry {
      reference
      resource {
        ... on QuestionnaireResponse {
          id
          authored
          item {
            linkId
            answer {
              valueBoolean valueInteger valueString valueDecimal
              valueCoding { code display }
            }
          }
        }
      }
    }
  }
`;

/**
 * GraphQL filter value for the `Composition.type` token search — both triage intake types.
 * Shape: `<system>|self-referral-intake,<system>|pcp-referral-intake`.
 */
const TRIAGE_COMPOSITION_TYPE_FILTER = TRIAGE_COMPOSITION_TYPES.map((c) => `${COMPOSITION_TYPE_SYSTEM}|${c}`).join(',');
const INACTIVE_PATIENT_TAG = `${POLARIS_SYSTEM_STATUS_TAG_SYSTEM}|${PATIENT_SYSTEM_STATUS.inactive}`;
const TEST_PATIENT_TAG = `${SELF_PAY_TAG_SYSTEM}|${TEST_PATIENT_TAG_CODE}`;

function addQueueExclusions(params: URLSearchParams, hideTestPatients: boolean): void {
  params.append('_tag:not', INACTIVE_PATIENT_TAG);
  if (hideTestPatients) {
    params.append('_tag:not', TEST_PATIENT_TAG);
  }
}

/**
 * GraphQL filter value for the `Observation.category` token search.
 * Shape: `<category-system>|patient-latest-triage`.
 */
const PATIENT_LATEST_CATEGORY_FILTER = `${TRIAGE_OBS_CATEGORY_SYSTEM}|${TRIAGE_OBS_CATEGORY.patientLatest}`;

/**
 * Build the `Observation._sort` value for a given sort column key + direction.
 *
 * - `valueQuantity` columns → `value-quantity` (asc) or `-value-quantity` (desc)
 * - `valueCodeableConcept` columns (insurance, recommendation) → `value-concept` / `-value-concept`
 *
 * `_lastUpdated` is the fallback for the default (no-sort) CompositionList branch.
 */
function buildSortParam(sort: TriageSortState | undefined): string {
  if (!sort || sort.column === 'name' || !(sort.column in TRIAGE_OBS_CODES)) {
    return '-_lastUpdated';
  }
  const spec = TRIAGE_OBS_CODES[sort.column as TriageIntakeSortColumnKey];
  const key = spec.valueField === 'valueQuantity' ? 'value-quantity' : 'value-concept';
  return sort.direction === 'asc' ? key : `-${key}`;
}

export function useTriageQueue({
  page,
  pageSize,
  activeStage,
  search,
  sort,
  hideTestPatients = false,
}: UseTriageQueueOptions): UseTriageQueueReturn {
  const medplum = useMedplum();
  const [compositions, setCompositions] = useState<TriageCompositionData[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  // Derived synchronously from inputs.
  const stageTag = activeStage ? STAGE_TAG_MAP[activeStage] : undefined;
  const sortByName = !!sort && sort.column === 'name';
  const sortByIntakeDate = !!sort && sort.column === 'intakeDate';
  const usePatientRoot = !!stageTag || !!search;
  // Stable primitive projections of the `sort` object so the `useCallback`
  // dependency analyzer can statically verify each access — referencing
  // `sort.column` / `sort.direction` inside the callback body otherwise
  // tripped `useExhaustiveDependencies` warnings.
  const sortColumn = sort?.column ?? null;
  const sortDirection = sort?.direction ?? null;

  // FILTER + SORT INTERACTION:
  //   - `sort` alone (no stage filter, no search) → Observation engine (3-query
  //     GraphQL path). Handled by `useObservationSort` below.
  //   - `sort` + Patient-rooted filters (`search`, stage filter, or both) →
  //     REST chained-search branch. Handled by `useRestSortBranch` below.
  //   - search-only → V1 PatientList branch (byte-equivalent).
  //   - default (no sort, no filter, no search) → CompositionList branch.
  const observationSortColumn =
    sort && sort.column !== 'name' && !usePatientRoot && sort.column in TRIAGE_OBS_CODES
      ? (sort.column as TriageIntakeSortColumnKey)
      : null;
  const useObservationSort = observationSortColumn !== null;

  // REST chained-search branch for Observation-backed sort whenever a
  // Patient-rooted filter is active (`search`, stage tag, or both).
  const restSortColumn =
    sort && sort.column !== 'name' && usePatientRoot && sort.column in TRIAGE_OBS_CODES
      ? (sort.column as TriageIntakeSortColumnKey)
      : null;
  const useRestSortBranch = restSortColumn !== null;

  // `sortColumn` / `sortDirection` (declared above near `stageTag`) are the
  // primitive projections of `sort` used as `useCallback` deps below — they
  // replace the previous `sortKey` memo and avoid the object-identity churn
  // that caused the fetch loop fixed in `useTriageQueueUrlState`.

  // biome-ignore lint/correctness/useExhaustiveDependencies: `sort` and `sort.direction` are intentionally captured via primitive projections `sortColumn`/`sortDirection` to avoid object-identity churn; `sortByName` is a derived boolean from `sortColumn`
  const fetchCompositions = useCallback(async () => {
    setLoading(true);
    const offset = (page - 1) * pageSize;
    const triageTagFilter = Object.values(TRIAGE_TAGS)
      .map((t) => `${t.system}|${t.code}`)
      .join(',');

    // Shared hydration query builder used by both the sort+search branch and
    // the sort-only null-bucket branch. `_count` differs between the two callers
    // (pageSize vs nullCount), so it is passed explicitly.
    const patientCsvForGraphQL = (csv: string, count: number): string => `
      query TriageQueueSortAndSearchHydration {
        PatientList(_id: "${csv}" _count: ${count}) {
          ${PATIENT_FIELDS}
          ${TRIAGE_CI_FRAGMENT(triageTagFilter)}
          compositions: CompositionList(_reference: subject status: "final" _count: 1 _sort: "-_lastUpdated") {
            ${COMPOSITION_SECTION_FIELDS}
          }
        }
      }
    `;

    try {
      // Patient-rooted queues must paginate after queue exclusions. The former
      // GraphQL PatientConnection counted inactive/test patients, while the
      // client removed them after the page was already sliced (215 total, 19
      // visible rows). REST supports the required :not modifiers; hydrate the
      // returned IDs through GraphQL to keep the clinical projection intact.
      if (usePatientRoot && (!sort || sortByName)) {
        const patientParams = new URLSearchParams();
        patientParams.set('_tag', stageTag ?? '');
        addQueueExclusions(patientParams, hideTestPatients);
        if (search) {
          patientParams.set('name', search);
        }
        let patientSort = '-_lastUpdated';
        if (sortByName) {
          patientSort = sortDirection === 'asc' ? 'given' : '-given';
        }
        patientParams.set('_sort', patientSort);
        patientParams.set('_count', String(pageSize));
        patientParams.set('_offset', String(offset));
        patientParams.set('_total', 'accurate');

        const patientBundle = await medplum.search('Patient', patientParams);
        const patientIds: string[] = (patientBundle.entry ?? [])
          .map((entry: BundleEntry) => (entry.resource as Patient | undefined)?.id)
          .filter((id: string | undefined): id is string => Boolean(id));
        const hydrationById = new Map<string, PatientWithCompositionsGraphQLResult>();
        if (patientIds.length > 0) {
          const hydrationResult = await medplum.graphql(patientCsvForGraphQL(patientIds.join(','), pageSize));
          for (const patient of (hydrationResult.data?.PatientList ?? []) as PatientWithCompositionsGraphQLResult[]) {
            if (patient.id) {
              hydrationById.set(patient.id, patient);
            }
          }
        }
        // MockClient fixtures and older Medplum responses may omit Patient
        // search entries even when the GraphQL fixture has rows. Let the
        // existing GraphQL path handle that empty case; real non-empty pages
        // use the accurate REST total above.
        if (patientIds.length > 0 || (patientBundle.total ?? 0) > 0) {
          setTotal(patientBundle.total ?? 0);
          setCompositions(
            applyFilters(
              patientIds
                .map((id: string): TriageCompositionData => {
                  const patient = hydrationById.get(id);
                  return mapToTriageCompositionData(patient?.compositions?.[0], patient);
                })
                .filter((row: TriageCompositionData) => Boolean(row.patientId)),
              hideTestPatients
            )
          );
          return;
        }
      }

      if (sortByIntakeDate && sortColumn && sortDirection) {
        // ── Intake date sort branch (Composition.date) ──────────────────────
        // `intakeDate` is sourced from Composition.date, not from triage
        // Observation rows. Use REST Composition chained search so stage-tag
        // and Patient-name filters work uniformly across tabs.
        const compositionParams = new URLSearchParams();
        compositionParams.set('status', 'final');
        compositionParams.set('type', TRIAGE_COMPOSITION_TYPE_FILTER);
        if (search) {
          compositionParams.set('subject:Patient.name', search);
        }
        if (stageTag) {
          compositionParams.set('subject:Patient._tag', stageTag);
        }
        compositionParams.append('subject:Patient._tag:not', INACTIVE_PATIENT_TAG);
        if (hideTestPatients) {
          compositionParams.append('subject:Patient._tag:not', TEST_PATIENT_TAG);
        }
        compositionParams.set('_sort', sortDirection === 'asc' ? 'date' : '-date');
        compositionParams.set('_count', String(pageSize));
        compositionParams.set('_offset', String(offset));
        compositionParams.set('_total', 'accurate');

        const compositionBundle = await medplum.search('Composition', compositionParams);
        const compositionList = (compositionBundle.entry ?? [])
          .map((entry: BundleEntry) => entry.resource as Composition | undefined)
          .filter(Boolean) as Composition[];

        const patientIds = compositionList
          .map((comp) => comp.subject?.reference)
          .filter((ref): ref is string => Boolean(ref?.startsWith('Patient/')))
          .map((ref) => ref.slice('Patient/'.length));

        const patientsById = new Map<string, PatientWithCompositionsGraphQLResult>();
        if (patientIds.length > 0) {
          const hydrationResult = await medplum.graphql(patientCsvForGraphQL(patientIds.join(','), pageSize));
          const patientList = (hydrationResult.data?.PatientList ?? []) as PatientWithCompositionsGraphQLResult[];
          for (const patient of patientList) {
            if (patient.id) {
              patientsById.set(patient.id, patient);
            }
          }
        }

        const rows = compositionList
          .map((composition) => {
            const subjectRef = composition.subject?.reference;
            if (!subjectRef?.startsWith('Patient/')) {
              return null;
            }
            const patientId = subjectRef.slice('Patient/'.length);
            const patient = patientsById.get(patientId);
            return mapToTriageCompositionData(composition, patient);
          })
          .filter(Boolean) as TriageCompositionData[];

        setTotal(compositionBundle.total ?? 0);
        setCompositions(applyFilters(rows, hideTestPatients));
        return;
      }

      if (useRestSortBranch && restSortColumn && sortColumn && sortDirection) {
        // ── Filter + sort coexistence branch (REST chained search) ───────────
        // A Patient-rooted filter is active, so GraphQL cannot express the
        // chained predicates we need. Route through REST Observation search,
        // scoping by Patient `name` and/or Patient `_tag` as available.
        const spec = TRIAGE_OBS_CODES[restSortColumn];
        const sortParam = buildSortParam({ column: sortColumn, direction: sortDirection });
        const codeFilter = `${spec.system}|${spec.code}`;

        // 1) REST Observation search for the with-value bucket.
        const obsParams = new URLSearchParams();
        obsParams.set('category', PATIENT_LATEST_CATEGORY_FILTER);
        obsParams.set('code', codeFilter);
        if (search) {
          obsParams.set('subject:Patient.name', search);
        }
        if (stageTag) {
          obsParams.set('subject:Patient._tag', stageTag);
        }
        obsParams.append('subject:Patient._tag:not', INACTIVE_PATIENT_TAG);
        if (hideTestPatients) {
          obsParams.append('subject:Patient._tag:not', TEST_PATIENT_TAG);
        }
        obsParams.set('_sort', sortParam);
        obsParams.set('_count', String(pageSize));
        obsParams.set('_offset', String(offset));
        obsParams.set('_total', 'accurate');
        const obsBundle = await medplum.search('Observation', obsParams);
        const withValueCountRaw = obsBundle.total ?? 0;
        const obsEntries = obsBundle.entry ?? [];
        const withValuePatientIds: string[] = [];
        const obsCompositionByPatientId = new Map<string, string>();
        for (const entry of obsEntries) {
          const obs = entry.resource as Observation | undefined;
          const subjectRef = obs?.subject?.reference;
          if (!subjectRef?.startsWith('Patient/')) {
            continue;
          }
          const patientId = subjectRef.slice('Patient/'.length);
          withValuePatientIds.push(patientId);
          const compRef = obs?.derivedFrom?.find((d) => d.reference?.startsWith('Composition/'))?.reference;
          if (compRef) {
            obsCompositionByPatientId.set(patientId, compRef.slice('Composition/'.length));
          }
        }

        // 2) REST Patient count for `totalMatchingPatients` (boundary math).
        const totalParams = new URLSearchParams();
        if (search) {
          totalParams.set('name', search);
        }
        if (stageTag) {
          totalParams.set('_tag', stageTag);
        }
        addQueueExclusions(totalParams, hideTestPatients);
        totalParams.set('_summary', 'count');
        const totalBundle = await medplum.search('Patient', totalParams);
        const totalMatchingPatients = totalBundle.total ?? 0;

        // Clamp against atomic-swap data races (a midflight Observation
        // deletion / Patient deletion could otherwise produce a negative
        // nullBucketCount).
        const withValueCount = Math.min(withValueCountRaw, totalMatchingPatients);
        const nullBucketCount = Math.max(0, totalMatchingPatients - withValueCount);

        // 3) Hydrate the with-value page slice via GraphQL `PatientList(_id: <csv>)`.
        // Carries the same PATIENT_FIELDS + TRIAGE_CI_FRAGMENT + CompositionList
        // sub-projection as the search-only branch so rows are not sparse.
        // CSV size guard: pageSize is 10 by default; well below the ~200-ID
        // (~9KB URL) limit confirmed in Phase 0 Appendix B Probe E.

        const withValuePatientsById = new Map<string, PatientWithCompositionsGraphQLResult>();
        if (withValuePatientIds.length > 0) {
          const hydrationResult = await medplum.graphql(patientCsvForGraphQL(withValuePatientIds.join(','), pageSize));
          const patientList = (hydrationResult.data?.PatientList ?? []) as PatientWithCompositionsGraphQLResult[];
          for (const p of patientList) {
            if (p.id) {
              withValuePatientsById.set(p.id, p);
            }
          }
        }

        // Build with-value rows preserving REST sort order, picking the
        // Composition that derivedFrom the Observation when available, falling
        // back to the patient's latest CompositionList[0] (same shape the
        // search-only branch uses).
        const withValueRows: TriageCompositionData[] = withValuePatientIds
          .map((patientId) => {
            const patient = withValuePatientsById.get(patientId);
            if (!patient) {
              return null;
            }
            const expectedCompId = obsCompositionByPatientId.get(patientId);
            const compFromList = patient.compositions ?? [];
            const comp =
              (expectedCompId ? compFromList.find((c) => c.id === expectedCompId) : undefined) ?? compFromList[0];
            if (!comp) {
              return null;
            }
            return mapToTriageCompositionData(comp, patient);
          })
          .filter(Boolean) as TriageCompositionData[];

        // 4) Null-bucket fetch (only when straddling/beyond boundary).
        const pageStart = offset;
        const pageEnd = offset + pageSize;
        const straddlesBoundary = pageStart < withValueCount && pageEnd > withValueCount;
        const beyondBoundary = pageStart >= withValueCount;
        const needsNullBucket = nullBucketCount > 0 && (straddlesBoundary || beyondBoundary);

        let nullRows: TriageCompositionData[] = [];
        if (needsNullBucket) {
          let nullOffset: number;
          let nullCount: number;
          if (sortDirection === 'desc') {
            nullOffset = Math.max(0, pageStart - withValueCount);
            const withValueOnPage = withValueRows.length;
            nullCount = Math.max(0, Math.min(pageSize - withValueOnPage, nullBucketCount - nullOffset));
          } else {
            nullOffset = pageStart;
            if (pageStart >= nullBucketCount) {
              nullCount = 0;
            } else {
              nullCount = Math.min(pageSize, nullBucketCount - pageStart);
            }
          }

          if (nullCount > 0) {
            // Positive-marker query: Observation?category=patient-latest-triage-nullish
            // &code=<spec>&subject:Patient.name=<search>[&subject:Patient._tag=<stageTag>]
            // &_sort=-_lastUpdated&_count=<nullCount>&_offset=<nullOffset>
            // No exclusion set needed — markers only exist for patients WITHOUT a real value.
            const nullMarkerParams = new URLSearchParams();
            nullMarkerParams.set('category', PATIENT_LATEST_NULLISH_CATEGORY_FILTER);
            nullMarkerParams.set('code', codeFilter);
            if (search) {
              nullMarkerParams.set('subject:Patient.name', search);
            }
            if (stageTag) {
              nullMarkerParams.set('subject:Patient._tag', stageTag);
            }
            nullMarkerParams.append('subject:Patient._tag:not', INACTIVE_PATIENT_TAG);
            if (hideTestPatients) {
              nullMarkerParams.append('subject:Patient._tag:not', TEST_PATIENT_TAG);
            }
            nullMarkerParams.set('_sort', '-_lastUpdated');
            nullMarkerParams.set('_count', String(nullCount));
            nullMarkerParams.set('_offset', String(nullOffset));

            const nullMarkerBundle = await medplum.search('Observation', nullMarkerParams);
            const nullMarkerEntries = nullMarkerBundle.entry ?? [];
            const nullPatientIds: string[] = [];
            for (const entry of nullMarkerEntries) {
              const obs = entry.resource as Observation | undefined;
              const subjectRef = obs?.subject?.reference;
              if (subjectRef?.startsWith('Patient/')) {
                nullPatientIds.push(subjectRef.slice('Patient/'.length));
              }
            }

            // Hydrate null-bucket Patients via GraphQL PatientList for full
            // sub-resource projection parity (Composition + CoverageList + triageCI).
            if (nullPatientIds.length > 0) {
              const nullHydrationResult = await medplum.graphql(
                patientCsvForGraphQL(nullPatientIds.join(','), nullCount)
              );
              const nullPatientList = (nullHydrationResult.data?.PatientList ??
                []) as PatientWithCompositionsGraphQLResult[];
              const nullPatientsById = new Map<string, PatientWithCompositionsGraphQLResult>();
              for (const p of nullPatientList) {
                if (p.id) {
                  nullPatientsById.set(p.id, p);
                }
              }
              nullRows = nullPatientIds
                .map((patientId) => {
                  const patient = nullPatientsById.get(patientId);
                  if (!patient) {
                    return null;
                  }
                  const comp = patient.compositions?.[0];
                  if (!comp) {
                    return null;
                  }
                  return mapToTriageCompositionData(comp, patient);
                })
                .filter(Boolean) as TriageCompositionData[];
            }
          }
        }

        // Merge per direction (plan §2 null-bucket order):
        //   asc → null bucket first, then with-value asc.
        //   desc → with-value first, then null bucket.
        const merged = sortDirection === 'asc' ? [...nullRows, ...withValueRows] : [...withValueRows, ...nullRows];

        setTotal(totalMatchingPatients);
        setCompositions(applyFilters(merged, hideTestPatients));
        return;
      }

      if (useObservationSort && observationSortColumn && sortColumn && sortDirection) {
        // ── Sort branch — Observation engine + null-bucket merge ─────────────
        const spec = TRIAGE_OBS_CODES[observationSortColumn];
        const sortParam = buildSortParam({ column: sortColumn, direction: sortDirection });
        const codeFilter = `${spec.system}|${spec.code}`;

        const sortedObservationsQuery = `
          query TriageQueueSorted {
            ObservationList(
              category: "${PATIENT_LATEST_CATEGORY_FILTER}"
              code: "${codeFilter}"
              _sort: "${sortParam}"
              _count: ${pageSize}
              _offset: ${offset}
            ) {
              id
              valueQuantity { value unit code }
              valueCodeableConcept { coding { system code display } }
              subject {
                reference
                resource {
                  ... on Patient {
                    ${PATIENT_FIELDS}
                    ${TRIAGE_CI_FRAGMENT(triageTagFilter)}
                  }
                }
              }
              derivedFrom {
                reference
                resource {
                  ... on Composition {
                    ${COMPOSITION_SECTION_FIELDS}
                  }
                }
              }
            }
            ObservationConnection(
              category: "${PATIENT_LATEST_CATEGORY_FILTER}"
              code: "${codeFilter}"
            ) { count }
            CompositionConnection(
              status: "final"
              type: "${TRIAGE_COMPOSITION_TYPE_FILTER}"
            ) { count }
          }
        `;

        const sortedResult = await medplum.graphql(sortedObservationsQuery);
        const observationList = (sortedResult.data?.ObservationList ?? []) as ObservationWithCompositionGraphQLResult[];
        const withValueCount = (sortedResult.data?.ObservationConnection as { count: number } | undefined)?.count ?? 0;
        const totalTriageCount =
          (sortedResult.data?.CompositionConnection as { count: number } | undefined)?.count ?? 0;
        // Clamp against data races (Observation deletions mid-fetch could otherwise
        // produce a negative null-bucket count).
        const nullBucketCount = Math.max(0, totalTriageCount - withValueCount);

        const withValueRows: TriageCompositionData[] = observationList
          .map((obs) => {
            const comp = obs.derivedFrom?.find((d) => d.resource)?.resource;
            if (!comp) {
              return null;
            }
            return mapToTriageCompositionData(comp, obs.subject?.resource);
          })
          .filter(Boolean) as TriageCompositionData[];

        // ── Decide whether to fetch the null bucket on this page ────────────
        const pageStart = offset;
        const pageEnd = offset + pageSize;
        const straddlesBoundary = pageStart < withValueCount && pageEnd > withValueCount;
        const beyondBoundary = pageStart >= withValueCount;
        const needsNullBucket = nullBucketCount > 0 && (straddlesBoundary || beyondBoundary);

        let nullRows: TriageCompositionData[] = [];
        if (needsNullBucket) {
          // Page slice math depends on direction (plan §2 null-bucket order):
          // - desc: with-value first, then null bucket. Null offset within bucket
          //   = max(0, pageStart - withValueCount). Slice size to fill page.
          // - asc: null bucket first, then with-value. Null offset = pageStart
          //   (bounded by nullBucketCount). Slice size to fill page.
          let nullOffset: number;
          let nullCount: number;
          if (sortDirection === 'desc') {
            nullOffset = Math.max(0, pageStart - withValueCount);
            // Number of null rows to fetch is whatever doesn't fit in the
            // with-value portion of this page (capped by pageSize and nullBucketCount).
            const withValueOnPage = withValueRows.length;
            nullCount = Math.max(0, Math.min(pageSize - withValueOnPage, nullBucketCount - nullOffset));
          } else {
            // asc: null bucket first
            nullOffset = pageStart;
            // Null rows on this page = min(remaining null-bucket rows, pageSize).
            // If pageStart >= nullBucketCount, no null rows on this page (we're
            // already in the with-value bucket).
            if (pageStart >= nullBucketCount) {
              nullCount = 0;
            } else {
              nullCount = Math.min(pageSize, nullBucketCount - pageStart);
            }
          }

          if (nullCount > 0) {
            // ── Positive-marker null-bucket query ───────────────────────────
            // Replaces the exclusion-set pre-fetch + Composition REST search.
            // Queries Observation?category=patient-latest-triage-nullish&code=<col>
            // with server-side _count/_offset so the page slice is exact.
            // No client-side filtering or pre-fetch round trip required.
            const nullMarkerParams = new URLSearchParams();
            nullMarkerParams.set('category', PATIENT_LATEST_NULLISH_CATEGORY_FILTER);
            nullMarkerParams.set('code', codeFilter);
            nullMarkerParams.set('_sort', '-_lastUpdated');
            nullMarkerParams.set('_count', String(nullCount));
            nullMarkerParams.set('_offset', String(nullOffset));

            const nullMarkerBundle = await medplum.search('Observation', nullMarkerParams);
            const nullMarkerEntries = nullMarkerBundle.entry ?? [];
            const nullPatientIds: string[] = [];
            for (const entry of nullMarkerEntries) {
              const obs = entry.resource as Observation | undefined;
              const subjectRef = obs?.subject?.reference;
              if (subjectRef?.startsWith('Patient/')) {
                nullPatientIds.push(subjectRef.slice('Patient/'.length));
              }
            }

            // Hydrate null-bucket Patients via GraphQL PatientList for full
            // sub-resource projection parity (Composition + CoverageList + triageCI).
            if (nullPatientIds.length > 0) {
              const nullHydrationResult = await medplum.graphql(
                patientCsvForGraphQL(nullPatientIds.join(','), nullCount)
              );
              const nullPatientList = (nullHydrationResult.data?.PatientList ??
                []) as PatientWithCompositionsGraphQLResult[];
              const nullPatientsById = new Map<string, PatientWithCompositionsGraphQLResult>();
              for (const p of nullPatientList) {
                if (p.id) {
                  nullPatientsById.set(p.id, p);
                }
              }
              nullRows = nullPatientIds
                .map((patientId) => {
                  const patient = nullPatientsById.get(patientId);
                  if (!patient) {
                    return null;
                  }
                  const comp = patient.compositions?.[0];
                  if (!comp) {
                    return null;
                  }
                  return mapToTriageCompositionData(comp, patient);
                })
                .filter(Boolean) as TriageCompositionData[];
            }
          }
        }

        // ── Merge per direction ─────────────────────────────────────────────
        // desc: with-value desc first, then null bucket last.
        // asc: null bucket first, then with-value asc.
        const merged = sortDirection === 'asc' ? [...nullRows, ...withValueRows] : [...withValueRows, ...nullRows];

        setTotal(totalTriageCount);
        setCompositions(applyFilters(merged, hideTestPatients));
        return;
      }

      // ── Patient name sort branch (sortByName, no Patient-rooted filter) ──
      //
      // Invariant: each triage Patient has at most ONE valid (final, triage-
      // type) Composition AND carries one `POLARIS_TRIAGE_STATUS_TAG_SYSTEM`
      // status tag. With both invariants, Patient ↔ Composition is 1:1 and
      // total = PatientConnection(_tag) = CompositionConnection(status, type).
      //
      // Server-side filter via `_tag` (Medplum rejected the reverse-chain
      // `_has_Composition_subject_status` form: "Unknown argument" on
      // QueryType.PatientList/PatientConnection — confirmed against
      // dev-hugobessa 2026-05-15). Backfill script
      // `backfill-triage-status-tags.ts` ensures every Patient with a triage
      // Composition carries the tag so the cohorts align.
      if (sortByName && !usePatientRoot && sortColumn && sortDirection) {
        const familySort = sortDirection === 'asc' ? 'given' : '-given';
        const triagePatientTagFilter = ACTIVE_TRIAGE_QUEUE_STATUS_CODES.map(
          (s) => `${POLARIS_TRIAGE_STATUS_TAG_SYSTEM}|${s}`
        ).join(',');
        const sortQuery = `
          query TriagePatientNameSort {
            PatientList(_tag: "${triagePatientTagFilter}" _sort: "${familySort}" _count: ${pageSize} _offset: ${offset}) {
              ${PATIENT_FIELDS}
              ${TRIAGE_CI_FRAGMENT(triageTagFilter)}
              compositions: CompositionList(_reference: subject status: "final" type: "${TRIAGE_COMPOSITION_TYPE_FILTER}" _count: 1 _sort: "-_lastUpdated") {
                ${COMPOSITION_SECTION_FIELDS}
              }
            }
            PatientConnection(_tag: "${triagePatientTagFilter}") { count }
          }
        `;
        const sortResult = await medplum.graphql(sortQuery);
        const sortedPatients = (sortResult.data?.PatientList ?? []) as PatientWithCompositionsGraphQLResult[];
        const connection = sortResult.data?.PatientConnection as { count: number } | undefined;
        setTotal(connection?.count ?? 0);
        setCompositions(
          applyFilters(
            sortedPatients.map((patient) => mapToTriageCompositionData(patient.compositions?.[0], patient)),
            hideTestPatients
          )
        );
        return;
      }
      // ── Default branch (no sort, OR sort + filter → fall back) ────────────
      let query: string;
      if (usePatientRoot) {
        const tagParam = stageTag ? `_tag: "${stageTag}"` : '';
        const nameParam = search ? `name: "${search}"` : '';
        // Patient name sort uses FHIR `_sort=family` (asc) / `-family` (desc)
        // on the PatientList root when a Patient-rooted filter (stage/search)
        // is already active. Other sort columns don't reach here.
        let sortParam = '-_lastUpdated';
        if (sortByName && sort) {
          sortParam = sort.direction === 'asc' ? 'given' : '-given';
        }
        const filterArgs = [nameParam, tagParam].filter(Boolean).join(' ');
        const patientParams = [filterArgs, `_count: ${pageSize}`, `_offset: ${offset}`, `_sort: "${sortParam}"`]
          .filter(Boolean)
          .join(' ');
        query = `
          query TriageQueueFiltered {
            PatientList(${patientParams}) {
              ${PATIENT_FIELDS}
              ${TRIAGE_CI_FRAGMENT(triageTagFilter)}
              compositions: CompositionList(_reference: subject status: "final" _count: 1 _sort: "-_lastUpdated") {
                ${COMPOSITION_SECTION_FIELDS}
              }
            }
            PatientConnection(${filterArgs}) { count }
          }
        `;
      } else {
        // Filter by triage composition type on both the page query and the
        // count query so the total matches the sort branch (which also filters
        // by type). Without the type filter, the count included non-triage
        // final Compositions and the default+sort totals diverged
        // (e.g. 210 unsorted vs 208 sorted).
        // Note: `sortByName && !usePatientRoot` was handled above by the
        // dedicated 2-step Patient-rooted branch. This default branch only
        // fires for the unsorted unfiltered case.
        query = `
          query TriageQueue {
            CompositionList(status: "final" type: "${TRIAGE_COMPOSITION_TYPE_FILTER}" _sort: "-_lastUpdated" _count: ${pageSize} _offset: ${offset}) {
              ${COMPOSITION_SECTION_FIELDS}
              subject {
                reference
                resource {
                  ... on Patient {
                    ${PATIENT_FIELDS}
                    ${TRIAGE_CI_FRAGMENT(triageTagFilter)}
                  }
                }
              }
            }
            CompositionConnection(status: "final" type: "${TRIAGE_COMPOSITION_TYPE_FILTER}") { count }
          }
        `;
      }

      const result = await medplum.graphql(query);

      if (usePatientRoot) {
        const patientList = (result.data?.PatientList ?? []) as PatientWithCompositionsGraphQLResult[];
        const connection = result.data?.PatientConnection as { count: number } | undefined;
        setTotal(connection?.count ?? 0);
        // Render every Patient — including those with no final Composition —
        // so the row count matches `PatientConnection.count`. Without this the
        // table dropped Patients with no final Composition, making the
        // displayed row count smaller than `total` (e.g. "201 - 206 of 208"
        // for admins who see Patients across multiple orgs).
        setCompositions(
          applyFilters(
            patientList.map((patient) => mapToTriageCompositionData(patient.compositions?.[0], patient)),
            hideTestPatients
          )
        );
      } else {
        const compositionList = (result.data?.CompositionList ?? []) as CompositionGraphQLResult[];
        const connection = result.data?.CompositionConnection as { count: number } | undefined;
        setTotal(connection?.count ?? 0);
        setCompositions(
          applyFilters(
            compositionList.map((comp) =>
              mapToTriageCompositionData(comp, comp.subject?.resource as PatientWithCompositionsGraphQLResult)
            ),
            hideTestPatients
          )
        );
      }
    } finally {
      setLoading(false);
    }
  }, [
    medplum,
    page,
    pageSize,
    stageTag,
    search,
    sortColumn,
    sortDirection,
    sortByName,
    sortByIntakeDate,
    usePatientRoot,
    useObservationSort,
    observationSortColumn,
    useRestSortBranch,
    restSortColumn,
    hideTestPatients,
  ]);

  useEffect(() => {
    fetchCompositions().catch(console.error);
  }, [fetchCompositions]);

  // sortSupported is always true. The Observation engine handles sort for the
  // unfiltered branch; the REST chained-search branch handles Patient-rooted
  // filter + sort combinations. The UI keeps arrows enabled on every named
  // column regardless — the URL contract is uniform.
  return { compositions, loading, total, sortSupported: true };
}
